import { useCallback, useEffect, useRef, useState } from 'react';

import type { Deal } from 'src/types';

import type { MakeItem } from '../makeApi';
import {
  getLanguageModel,
  isAvailableStatus,
  isDownloadableStatus,
  SESSION_OPTIONS,
  type LanguageModelSession,
} from './promptApi';
import { matchFastPath } from './fastPath';
import { lookupCurrentFaq } from './faqCatalog';
import {
  buildRegistry,
  type CartSavings,
  type PriceComparison,
  type ToolContext,
  type ToolSpec,
} from './tools';

// Strips fenced code blocks from a free-text final answer.
const stripFences = (text: string) => text.replace(/```[\s\S]*?```/g, '').trim();

const STORAGE_KEY = 'sac-make-chat';

export type ChatStatus = 'checking' | 'unsupported' | 'unavailable' | 'ready' | 'error';

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  // Rich attachments — exactly one is set on an assistant message that renders cards.
  items?: MakeItem[];
  deals?: Deal[];
  comparison?: PriceComparison;
  cartSavings?: CartSavings;
}

interface Options {
  onAddItems: (items: MakeItem[]) => void;
  onSearch: (
    vendor: string,
    keywords: string,
    region?: string,
    zip?: string,
  ) => Promise<MakeItem[]>;
  getCartItems: () => MakeItem[];
  onRemoveIndexes: (indexes: number[]) => void;
  onSetQuantity: (index: number, qty: number) => void;
  onClearCart: () => void;
  onSaveCart: () => void;
  vendor?: string;
}

// Small on-device context window — keep tool results tight. The Prompt API
// itself drops the oldest turns (keeping the system prompt) on overflow.
const TOOL_RESULT_LIMIT = 1500;

const SYSTEM_PREAMBLE =
  'You are a shopping assistant embedded in the Share-A-Cart "make a list" page. ' +
  'Help the user find products and add them to their cart using the available tools. ' +
  'When you search, the results appear to the user as numbered product cards right here in the chat, ' +
  'each with an Add button — you do NOT need to relist them. After a search, briefly ask which to add. ' +
  'Item numbers always refer to the most recent search list; NEVER pass a list number as a product id ' +
  'to another tool. To add items, call add_to_cart with their numbers. To compare an item\'s price ' +
  'across retailers, call compare_price with its number. The user saves the cart with the Save button. ' +
  'For questions about how Share-A-Cart works (sharing, accounts, price, privacy, mobile, stores, receiving a cart), call answer_faq and answer only from its result. Do not invent how Share-A-Cart works. ' +
  'Keep replies short and friendly.';

const ACTIVITY_LABELS: Record<string, string> = {
  search_products: 'Searching products…',
  add_to_cart: 'Adding to cart…',
  add_cheapest: 'Finding the cheapest…',
  add_by_url: 'Adding from link…',
  compare_price: 'Comparing prices…',
  compare_cart_item: 'Price-checking your cart…',
  compare_cart: 'Price-checking your cart…',
  view_cart: 'Checking your cart…',
  remove_from_cart: 'Removing from cart…',
  set_quantity: 'Updating quantity…',
  clear_cart: 'Clearing cart…',
  save_cart: 'Saving your cart…',
  find_deals: 'Finding deals…',
  answer_faq: 'Checking FAQs…',
  list_retailers: 'Checking retailers…',
  product_details: 'Getting details…',
};

function buildToolSignatures(tools: ToolSpec[]): string {
  return tools
    .map((t) => {
      const schema = t.inputSchema as {
        properties?: Record<string, { type?: string }>;
        required?: string[];
      };
      const props = schema.properties || {};
      const required = schema.required || [];
      const params = Object.entries(props)
        .map(([name, def]) => `${name}${required.includes(name) ? '' : '?'}: ${def.type || 'any'}`)
        .join(', ');
      return `- ${t.name}(${params})`;
    })
    .join('\n');
}

function buildSystemPrompt(tools: ToolSpec[]): string {
  return [SYSTEM_PREAMBLE, '', 'You can use these tools:', buildToolSignatures(tools)].join('\n');
}

type Decision =
  | { kind: 'reply'; text: string }
  | { kind: 'tool'; name: string; args: Record<string, unknown> };

function extractJson(s: string): string {
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  let t = (fence ? fence[1] : s).trim();
  const start = t.indexOf('{');
  const end = t.lastIndexOf('}');
  if (start >= 0 && end > start) t = t.slice(start, end + 1);
  return t;
}

function tryParse(s: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(extractJson(s));
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function promptText(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && 'value' in raw) {
    const value = (raw as { value?: unknown }).value;
    if (typeof value === 'string') return value;
  }
  return '';
}

// `loaded` is a 0–1 fraction and `total` is 1. Some events omit `total`.
function downloadFraction(ev: ProgressEvent): number | null {
  if (typeof ev.loaded !== 'number' || !Number.isFinite(ev.loaded)) return null;
  const fraction = ev.total > 1 ? ev.loaded / ev.total : ev.loaded;
  if (!Number.isFinite(fraction)) return null;
  return Math.min(1, Math.max(0, fraction));
}

const ARG_KEYS = [
  'vendor',
  'keywords',
  'numbers',
  'quantity',
  'url',
  'number',
  'vendors',
  'retailer',
  'question',
  'max_price',
  'count',
  'itemid',
  'limit',
] as const;

function decisionSchema(tools: ToolSpec[]): object {
  const properties: Record<string, object> = {
    tool: { type: 'string', enum: [...tools.map((t) => t.name), 'reply'] },
    reply: { type: 'string' },
  };
  for (const key of ARG_KEYS) properties[key] = { type: 'string' };
  return { type: 'object', additionalProperties: false, properties, required: ['tool'] };
}

function argsFromDecision(d: Record<string, unknown>): Record<string, unknown> {
  const args: Record<string, unknown> = {};
  for (const key of ARG_KEYS) {
    const value = d[key];
    if (typeof value === 'string' && value.trim()) args[key] = value.trim();
    else if (typeof value === 'number' && Number.isFinite(value)) args[key] = value;
  }
  return args;
}

// One model call picks the tool and its arguments together. A second call to
// "provide the JSON arguments" was slower and often dropped the user's words.
async function constrainedDecide(
  session: LanguageModelSession,
  tools: ToolSpec[],
  turnInput: string,
  signal: AbortSignal,
): Promise<Decision> {
  const hint =
    'Choose one tool and fill only the argument fields it needs. ' +
    'To speak to the user, set tool to "reply" and write reply.';
  let raw: string;
  try {
    raw = promptText(
      await session.prompt(`${hint}\n\nUser: ${turnInput}`, {
        responseConstraint: decisionSchema(tools),
        omitResponseConstraintInput: true,
        signal,
      }),
    );
  } catch (err) {
    if (signal.aborted) throw err;
    raw = promptText(
      await session.prompt(
        `${hint}\nReply with JSON {"tool":"<name or reply>","reply":"<text>"}.\n\nUser: ${turnInput}`,
        { signal },
      ),
    );
  }
  const d = tryParse(raw);
  if (!d || typeof d.tool !== 'string') {
    const text = raw.trim();
    if (!text || text.startsWith('{') || text.startsWith('[') || text.startsWith('```')) {
      return { kind: 'reply', text: '' };
    }
    return { kind: 'reply', text };
  }

  const spec = tools.find((t) => t.name === d.tool);
  if (spec) return { kind: 'tool', name: d.tool, args: argsFromDecision(d) };

  const reply = typeof d.reply === 'string' ? d.reply.trim() : '';
  // The model was asked for JSON. Never show that object as the chat message.
  if (reply && !reply.startsWith('{') && !reply.startsWith('[')) {
    return { kind: 'reply', text: reply };
  }
  return { kind: 'reply', text: '' };
}

function loadMessages(): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as ChatMessage[]) : [];
  } catch {
    return [];
  }
}

export function usePromptApiChat({
  onAddItems,
  onSearch,
  getCartItems,
  onRemoveIndexes,
  onSetQuantity,
  onClearCart,
  onSaveCart,
  vendor,
}: Options) {
  const [status, setStatus] = useState<ChatStatus>('checking');
  const [messages, setMessages] = useState<ChatMessage[]>(loadMessages);
  const [activity, setActivity] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);

  const sessionRef = useRef<LanguageModelSession | null>(null);
  const toolsRef = useRef<ToolSpec[] | null>(null);
  const lastSearchRef = useRef<MakeItem[]>([]);
  const pendingConfirmRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const idRef = useRef(messages.reduce((mx, m) => Math.max(mx, m.id), 0));
  const onAddRef = useRef(onAddItems);
  const onSearchRef = useRef(onSearch);
  const vendorRef = useRef(vendor);
  const cartApiRef = useRef({ getCartItems, onRemoveIndexes, onSetQuantity, onClearCart, onSaveCart });
  onAddRef.current = onAddItems;
  onSearchRef.current = onSearch;
  vendorRef.current = vendor;
  cartApiRef.current = { getCartItems, onRemoveIndexes, onSetQuantity, onClearCart, onSaveCart };

  // Persist the conversation so a page reload keeps context and cards.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      /* storage unavailable */
    }
  }, [messages]);

  const append = useCallback((role: ChatMessage['role'], content: string) => {
    setMessages((prev) => [...prev, { id: (idRef.current += 1), role, content }]);
  }, []);

  const appendAttachment = useCallback((attachment: Partial<ChatMessage>) => {
    setMessages((prev) => [
      ...prev,
      { id: (idRef.current += 1), role: 'assistant', content: '', ...attachment },
    ]);
  }, []);

  const checkAvailability = useCallback(async () => {
    const lm = getLanguageModel();
    if (!lm) {
      setStatus('unsupported');
      return;
    }
    try {
      const avail = await lm.availability(SESSION_OPTIONS);
      setStatus(
        isAvailableStatus(avail) || isDownloadableStatus(avail) ? 'ready' : 'unavailable',
      );
    } catch {
      setStatus('unavailable');
    }
  }, []);

  useEffect(() => {
    void checkAvailability();
  }, [checkAvailability]);

  const ensureRegistry = useCallback((): ToolSpec[] => {
    if (!toolsRef.current) toolsRef.current = buildRegistry();
    return toolsRef.current;
  }, []);

  const ctxRef = useRef<ToolContext>({
    getLastSearch: () => lastSearchRef.current,
    setLastSearch: (items) => {
      lastSearchRef.current = items;
    },
    appendResults: (items) => appendAttachment({ items }),
    appendDeals: (deals) => appendAttachment({ deals }),
    appendComparison: (comparison) => appendAttachment({ comparison }),
    appendCartSavings: (cartSavings) => appendAttachment({ cartSavings }),
    getCartVendor: () => vendorRef.current,
    pageSearch: (v, k, r, z) => onSearchRef.current(v, k, r, z),
    addItems: (items) => onAddRef.current(items),
    setPendingConfirm: (msg) => {
      pendingConfirmRef.current = msg;
    },
    getCartItems: () => cartApiRef.current.getCartItems(),
    removeCartIndexes: (idx) => cartApiRef.current.onRemoveIndexes(idx),
    setCartQuantity: (i, q) => cartApiRef.current.onSetQuantity(i, q),
    clearCart: () => cartApiRef.current.onClearCart(),
    saveCart: () => cartApiRef.current.onSaveCart(),
  });

  const runTool = useCallback(
    async (name: string, args: Record<string, unknown>): Promise<string> => {
      const spec = toolsRef.current?.find((t) => t.name === name);
      if (!spec) return `Unknown tool "${name}".`;
      const result = await spec.execute(args, ctxRef.current);
      return result.length > TOOL_RESULT_LIMIT
        ? `${result.slice(0, TOOL_RESULT_LIMIT)}\n…(truncated)`
        : result;
    },
    [],
  );

  const ensureSession = useCallback(async (): Promise<LanguageModelSession> => {
    const lm = getLanguageModel();
    if (!lm) throw new Error('Prompt API unavailable');

    const specs = ensureRegistry();
    if (sessionRef.current) return sessionRef.current;

    // One create() only. While Gemini Nano is still downloadable, Chrome
    // consumes the user gesture inside create(). A second call (the old
    // temperature/topK fallback) then fails with NotAllowedError and the
    // chat shows the "could not reach the model" message.
    // `loaded` is a 0–1 fraction (total is 1). Older builds reported bytes.
    sessionRef.current = await lm.create({
      ...SESSION_OPTIONS,
      initialPrompts: [{ role: 'system', content: buildSystemPrompt(specs) }],
      monitor: (m) => {
        m.addEventListener('downloadprogress', (e) => {
          const fraction = downloadFraction(e as ProgressEvent);
          if (fraction != null) setDownloadProgress(fraction);
        });
      },
    });
    setDownloadProgress(null);
    return sessionRef.current;
  }, [ensureRegistry]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Starts a fresh conversation: aborts any run, drops the model session (so
  // context resets), and clears the transcript + persisted history.
  const newChat = useCallback(() => {
    abortRef.current?.abort();
    sessionRef.current?.destroy();
    sessionRef.current = null;
    lastSearchRef.current = [];
    pendingConfirmRef.current = null;
    setActivity(null);
    setBusy(false);
    setMessages([]);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;

      append('user', trimmed);
      pendingConfirmRef.current = null;

      // Deterministic fast-path: unambiguous commands run a tool directly.
      const fast = matchFastPath(trimmed);
      if (fast) {
        setBusy(true);
        setActivity(ACTIVITY_LABELS[fast.tool] ?? null);
        try {
          ensureRegistry();
          const result = await runTool(fast.tool, fast.args);
          append('assistant', pendingConfirmRef.current || fast.confirm || result);
        } catch (err) {
          console.error('[make-chat fast-path]', err);
          append('assistant', 'Sorry — that didn’t work. Try rephrasing.');
        } finally {
          setBusy(false);
          setActivity(null);
        }
        return;
      }

      const faqHit = lookupCurrentFaq(trimmed);
      if (faqHit) {
        append('assistant', faqHit.text);
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const { signal } = controller;

      setBusy(true);
      setActivity('Thinking…');

      try {
        const session = await ensureSession();
        const tools = ensureRegistry();
        if (signal.aborted) return;

        const decision = await constrainedDecide(session, tools, trimmed, signal);
        if (signal.aborted) return;

        if (decision.kind === 'reply') {
          append('assistant', stripFences(decision.text) || 'What would you like to find?');
          return;
        }

        setActivity(ACTIVITY_LABELS[decision.name] || `Running ${decision.name}…`);
        const result = await runTool(decision.name, decision.args).catch((e) => {
          console.error('[make-chat tool]', e);
          return 'Sorry — that didn’t work. Try again in a moment.';
        });
        if (signal.aborted) return;

        // Tool text is often instructions for the model. Prefer the short
        // confirmation the tool wrote for the user.
        const modelNote = /do NOT|For your reference only|Tell the user/.test(result);
        append(
          'assistant',
          pendingConfirmRef.current || (modelNote ? 'Done.' : stripFences(result)),
        );
      } catch (err) {
        if (!signal.aborted) {
          console.error('[make-chat]', err);
          sessionRef.current?.destroy();
          sessionRef.current = null;
          append(
            'assistant',
            'Sorry — I could not reach the on-device model. On desktop Chrome, enable the built-in AI flags (chrome://flags) and make sure Gemini Nano has finished downloading.',
          );
        }
      } finally {
        setBusy(false);
        setActivity(null);
        setDownloadProgress(null);
        abortRef.current = null;
      }
    },
    [busy, ensureSession, ensureRegistry, append, runTool],
  );

  return {
    status,
    messages,
    busy,
    activity,
    downloadProgress,
    send,
    stop,
    newChat,
    retry: checkAvailability,
  };
}
